import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { SubprojectEditComponent } from '../subproject-edit/subproject-edit.component';
import { modalConfig, ModalEnum } from 'src/app/core/util/modalConfig';
import { Permission } from "../../../../core/enum/permission";
import { Globals } from 'src/app/globals';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubprojectQuery } from 'src/app/akita/subproject/state/subprojects.query';
import TileSource from 'ol/source/Tile';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SubprojectHierarchyLimitModalComponent } from '../subproject-hierarchy-limit-modal/subproject-hierarchy-limit-modal.component';
import { translate } from '@jsverse/transloco';
import { GenericComponent } from 'src/app/core/util/abstract/generic-component';
import { Router } from '@angular/router';


@Component({
  selector: 'app-generic-multiselect-subprojects',
  templateUrl: './generic-multiselect-subprojects.component.html',
  styleUrls: ['./generic-multiselect-subprojects.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenericMultiselectSubprojectsComponent extends GenericComponent implements OnChanges, OnInit, OnDestroy {
  @Input() uniqueId: string = '';
  @Input() userSubprojects: boolean = false;
  @Input() select: boolean = false;
  @Input() createSubproject: boolean = false;
  @Input() multiSelect: boolean = false; 
  @Input() disabled: boolean = true; 
  @Input() label: string = 'Subproject';
  @Input() subprojects: any;
  @Input() currentSubProject: any;
  @Input() alreadySelected: any;
  @Input() editSubproject: any;
  @Input() moduleName: any = '';
  @Input() selectParentWhenChildSelected: boolean = true;
  @Input() maxChipItems: any = 3;
  @Input() subprojectRoles: any = [];
  @Input() selectedSubprojectRoles: any = [];
  @Input() userSubprojectsRoles: any = [];
  @Output() selectedItems: EventEmitter<any> = new EventEmitter<any>();
  @Output() switchProject: EventEmitter<any> = new EventEmitter<any>();
  @Output() onInputFocused = new EventEmitter<void>();
  @ViewChild('mySelect', { read: ElementRef }) inputElement: ElementRef;
  @ViewChild('dropdown') dropdownRef: ElementRef;
  public Permission = Permission;
  _selectedProjectId: number | null = null;
  selectedProjects: {[id: number]: boolean} = {};
  selectedItemsArray: any[] = [];
  allSubprojects: any[] = [];
  completeEditSubprojectNode: any;
  showDropdown = false;
  searchTerm = '';
  activeSubProject: any;
  private activeSubProjectSubscription: Subscription;
  // eslint-disable-next-line no-empty-function
  private onChange: (value: any) => void = () => {};
  // For tracking expanded nodes during search
  private expandedNodes: any[] = [];
  
  // Original data backup
  private originalData: any[];
  
  // Search subject
  private searchSubject = new BehaviorSubject<string>('');

  AvatarCustomStyleSelector = {
    "border-width": "3px",
    "font-weight": "bold",
    width: "27px",
  };
  AvatarCustomStyle = {
    "border-width": "3px",
    "font-weight": "bold",
    width: "37px",
  };
  SmallAvatarCustomStyle = {
    "border-width": "3px",
    "font-weight": "bold",
    width: "25px",
  };

  private _transformer = (node: any, level: number) => {
    return {
      ...node,
      expandable: !!node.children && node.children.length > 0,
      level: level,
    };
  };
  treeControl = new FlatTreeControl<any>(
    node => node.level,
    node => node.expandable,
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.children,
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  hasChild = (_: number, node: any) => node.expandable;

  constructor(private dialog: MatDialog, public globals: Globals, private eRef: ElementRef, private subProjectQuery: SubprojectQuery, private cd: ChangeDetectorRef, public snackbar: MatSnackBar, private router: Router) {
    super(globals);
    this.originalData = this.reorderSubprojects();
    this.dataSource.data = this.reorderSubprojects();
    this.activeSubProject = this.subProjectQuery.getActive();
    this.allSubprojects = this.subProjectQuery.getAll();
    // Keep radio checked state in sync with store
    this.activeSubProjectSubscription = this.subProjectQuery.selectActive().subscribe((active) => {
      this.activeSubProject = active;
      this.cd.markForCheck();
    });
    // Setup search filtering - works for both isSubprojectList true and false
    this.searchSubject.pipe(
      map(searchTerm => {
        if (!searchTerm) {
          return this.originalData;
        }
        // Store expanded nodes before filtering
        this.expandedNodes = [];
        this.treeControl.dataNodes.forEach(node => {
          if (this.treeControl.isExpanded(node)) {
            this.expandedNodes.push(node);
          }
        });
        return this.filterTree(this.originalData, searchTerm.toLowerCase());
      })
    ).subscribe(filteredData => {
      this.dataSource.data = filteredData;
    });
  }

  ngOnChanges() {
    if (!this.select) {
      this.originalData = this.reorderSubprojects();
      this.dataSource.data = this.reorderSubprojects();
      this.expandPathToActiveSubProject();
    }
    if (this.subprojects && this.subprojects.length) {
      this.subprojects.forEach((item: any) => {
        this.selectedProjects[item.id] = true;
      });
    }
    if (this.userSubprojects) {
      this.originalData = this.globals.subprojects;
      this.dataSource.data = this.globals.subprojects;
      setTimeout(() => {
        this.treeControl.dataNodes.forEach(node => {
          if (this.treeControl.isExpandable(node)) {
            this.treeControl.expand(node);
          }
        });
      });
    }
    this.reorderSubprojects()
  }

  ngOnInit(){
    if (this.alreadySelected) {
      this._selectedProjectId = this.alreadySelected.parent_id;
    }
    if (this.select && !this.multiSelect) {
      this.disabled = false;
      this.completeEditSubprojectNode = this.treeControl.dataNodes.find(
        node => node.id === this.editSubproject.id
      );
    }
  }

  ngOnDestroy(): void {
    this.activeSubProjectSubscription?.unsubscribe();
  }

  reorderSubprojects() {
    // Filter out shared projects if in production
    let subprojects = [...this.globals.subprojects];
    if (!this.hasModule('share')) {
      subprojects = subprojects.filter(item => item.tenant_type !== "shared");
    }

    // If subprojects module is not enabled, filter out non-shared subprojects
    if (!this.hasModule('subprojects')) {
      subprojects = subprojects.filter(item => item.tenant_type == "shared");
    }
    
    const reordered = subprojects.sort((a, b) => {
      const aType = a.tenant_type ?? null;
      const bType = b.tenant_type ?? null;
      if (!aType && bType) return -1;
      if (aType && !bType) return 1;
      if (aType === "shared" && bType !== "shared") return 1;
      if (aType !== "shared" && bType === "shared") return -1;

      return 0;
    });
    
    const firstSharedIndex = reordered.findIndex(item => item.tenant_type === "shared");
    if (firstSharedIndex !== -1) {
      reordered[firstSharedIndex] = {
        ...reordered[firstSharedIndex],
        is_first_shared: true
      };
    }
    return reordered;
  }

  expandPathToActiveSubProject(): void {
    const activeSubProject = this.getActiveSubProject();
    if (!activeSubProject?.id) return;
    const matchingNode = this.treeControl.dataNodes.find(
      node => `${node.id}` === `${activeSubProject.id}`
    );
    if (matchingNode) {
      this.expandParentNodes(matchingNode);
    }
  }

  public isActiveSubProject(node: any): boolean {
    const activeSubProject = this.getActiveSubProject();
    if (activeSubProject?.id) {
      return `${activeSubProject.id}` === `${node?.id}`;
    }

    return !!node?.code && this.router.url.includes(`/sub/${node.code}`);
  }

  private getActiveSubProject(): any {
    return this.currentSubProject ?? this.activeSubProject;
  }
  
  private expandParentNodes(node: any): void {
    if (!node.parent_id) return;
    const parentNode = this.treeControl.dataNodes.find(n => n.id === node.parent_id);
    if (parentNode) {
      this.treeControl.expand(parentNode);
      this.expandParentNodes(parentNode);
    }
  }

  private filterTree(nodes: any[], searchTerm: string): any[] {
    const filteredNodes = nodes.map(node => ({...node})).filter(node => {
      const newNode = {...node};
      if (newNode.children) {
        newNode.children = this.filterTree(newNode.children, searchTerm);
      }
      return newNode.name.toLowerCase().includes(searchTerm) || 
            (newNode.children && newNode.children.length > 0);
    });
    if (searchTerm) {
      setTimeout(() => {
        this.expandMatchingNodes(searchTerm);
      });
    }
    return filteredNodes;
  }

  private expandMatchingNodes(searchTerm: string): void {
    this.treeControl.collapseAll();
    const matchingNodes = this.treeControl.dataNodes.filter(node => 
      node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      this.hasMatchingChildren(node, searchTerm.toLowerCase())
    );
    matchingNodes.forEach(node => {
      this.expandParentNodes(node);
    });
  }

  private hasMatchingChildren(node: any, searchTerm: string): boolean {
    if (!node.children) return false;
    if (node.children.some((child: any) => 
      child.name.toLowerCase().includes(searchTerm))) {
      return true;
    }
    return node.children.some((child: any) => 
      this.hasMatchingChildren(child, searchTerm)
    );
  }
  
  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm = value;
    this.searchSubject.next(value);
    this.adjustDropdownPosition();
  }

  private flattenTree = (node: any): any[] => {
    return [node, ...(node.children ? node.children.flatMap(this.flattenTree) : [])];
  };

  clearSearch(): void {
    this.searchTerm = '';
    this.searchSubject.next(this.searchTerm);
  }
  
  onProjectSelect(node: any, isChecked: boolean): void {
    if (this.searchTerm.trim().length) {
      this.clearSearch();
    }
    if (!this.multiSelect) {
      // Single selection mode
      if (isChecked) {
        if (this.editSubproject && this.wouldExceedMaxDepth(node, this.completeEditSubprojectNode)) {
          const parentName = this.getRootParentName(node); 
          const message = translate('subprojects.hierarchy')+parentName+translate('subprojects.hierarchy_level')
          this.dialog.open(SubprojectHierarchyLimitModalComponent, {
            data: {
              text: message
            },
            width: "30vw",
          });
          return;
        }
        // Clear previous selection
        if (this._selectedProjectId !== null) {
          this.selectedProjects[this._selectedProjectId] = false;
        }
        this._selectedProjectId = node.id;
        this.selectedProjects[node.id] = true;
        this.selectedItems.emit(node); // Emit single item
      } else {
        this._selectedProjectId = null;
        this.selectedProjects[node.id] = false;
        this.updateSelectedItemsArray();
      }
    } else {
      // Multi selection mode
      this.selectedProjects[node.id] = isChecked;
      
      // In multi-select branch:
      if (isChecked) {
        if (this.selectParentWhenChildSelected) {
          this.selectParentNodes(node); // Will auto-update at end
        }
      } else {
        this.unselectChildNodes(node); // Will auto-update at end
      }
      // Update the selected items array
      this.updateSelectedItemsArray();
    }
    this.cd.detectChanges();
  }

  getRootParentName(node: any): string {
    const rootParent = this.getRootParent(node);
    return rootParent?.name || '';
  }

  getRootParent(node: any): any {
    if (!node || node.parent_id === null) {
        return node;
    }
    let current = node;
    while (current.parent_id !== null) {
        const parent = this.getParentNode(current);
        if (!parent) {
            return current;
        }
        current = parent;
    }

    return current;
  }

  private wouldExceedMaxDepth(proposedParent: any, childNode: any, maxDepth: number = 5): boolean {
    const parentDepth = this.treeControl.getLevel(proposedParent);
    const childDepth = this.getMaxDepthBelow(childNode);
    return (parentDepth + 2 + childDepth) > maxDepth;
  }

  private getMaxDepthBelow(node: any): number {
    if (!node.children || node.children.length === 0) {
      return 0;
    }
    let maxDepth = 0;
    const childNodes = this.treeControl.dataNodes.filter(n => n.parent_id === node.id);
    for (const child of childNodes) {
      const childDepth = this.getMaxDepthBelow(child);
      maxDepth = Math.max(maxDepth, childDepth + 1);
    }
    return maxDepth;
  }
  
  private updateSelectedItemsArray(): void {
    // Get all nodes from original data (not just filtered view)
    const allNodes = this.originalData.flatMap(this.flattenTree);
    // Filter selected nodes and map to remove children property
    this.selectedItemsArray = allNodes
      .filter(node => this.selectedProjects[node.id])
      .map(({ children, ...rest }) => rest);
    
    // Emit the array of selected items
    this.selectedItems.emit(this.selectedItemsArray);
    this.onChange(this.selectedItemsArray);
  }
  
  // Recursively select parent nodes
  private selectParentNodes(node: any): void {
    if (!node.parent_id) return;
    
    const parentNode = this.treeControl.dataNodes.find(n => n.id === node.parent_id);
    if (parentNode) {
        this.selectedProjects[parentNode.id] = true;
        this.selectParentNodes(parentNode); // No update for recursive calls
    }
  }
  
  // Recursively unselect child nodes
  private unselectChildNodes(node: any): void {
    const childNodes = this.treeControl.dataNodes.filter(n => n.parent_id === node.id);
    childNodes.forEach(child => {
      this.selectedProjects[child.id] = false;
      this.unselectChildNodes(child); // Pass false to prevent intermediate updates
    });
    this.cd.detectChanges();
  }
  
  // Check if a node is selected
  isSelected(node: any): boolean {
    if (!this.multiSelect) {
      return this._selectedProjectId === node.id;
    }
    return !!this.selectedProjects[node.id];
  }

  public edit(subProject): void {
    localStorage.setItem('isDialogOpen','true');
    this.dialog.open(
      SubprojectEditComponent,
      modalConfig(
        {
          data: subProject,
          disableClose: true,
          panelClass: 'customized-dialog-container',
        },
        ModalEnum.ModalDefault
      )
    ).afterClosed().subscribe(()=>{
      this.snackbar.dismiss();
      localStorage.removeItem('isDialogOpen');
    });
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  getSelectedProjectIds(): number[] {
    if (!this.multiSelect) {
      return this._selectedProjectId && this.allSubprojects.some(node => node.id === this._selectedProjectId)
        ? [this._selectedProjectId]
        : [];
    }
    const selectedIds = Object.keys(this.selectedProjects)
      .filter(id => this.selectedProjects[id])
      .map(id => +id);
    const validIds = selectedIds.filter(id =>
      this.allSubprojects.some(node => node.id === id)
    );
    return validIds;
  }

  getProjectName(id: number): string {
    const node = this.allSubprojects.find(n => n.id === id);
    return node ? node.name : '';
  }

  isChildOfEditSubproject(node: any): boolean {
    return !!this.editSubproject && this.isDescendant(this.completeEditSubprojectNode, node);
  }
  
  isDescendant(parent: any, target: any): boolean {
    if (!parent.children) return false;
    return parent.children.some(
      child => child.id === target.id || this.isDescendant(child, target)
    );
  } 

  isLastSibling(node: any): boolean {
    const parent = this.getParentNode(node);
    if (!parent) {
      const siblings = this.treeControl.dataNodes.filter(n => this.treeControl.getLevel(n) === 0);
      return siblings.indexOf(node) === siblings.length - 1;
    }
    const siblings = this.treeControl.dataNodes.filter(n => 
      this.getParentNode(n)?.id === parent.id && 
      this.treeControl.getLevel(n) === this.treeControl.getLevel(node)
    );
    
    return siblings.indexOf(node) === siblings.length - 1;
  }
  
  getSiblings(node: any): any[] {
    const parent = this.getParentNode(node);
    const currentLevel = this.treeControl.getLevel(node);
    
    if (!parent) {
      return this.treeControl.dataNodes.filter(n => 
        this.treeControl.getLevel(n) === 0
      );
    }
    return this.treeControl.dataNodes.filter(n => {
      const nParent = this.getParentNode(n);
      return nParent?.id === parent.id && 
             this.treeControl.getLevel(n) === currentLevel;
    });
  }
  
  getParentNode(node: any): any {
    const currentLevel = this.treeControl.getLevel(node);
    if (currentLevel < 1) return null;
    const nodeIndex = this.treeControl.dataNodes.indexOf(node);
    for (let i = nodeIndex - 1; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (this.treeControl.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  isChildOfLastSibling(node: any): boolean {
    if (this.treeControl.getLevel(node) <= 1) return false;
    const parent = this.getParentNode(node);
    if (!parent) return false;
    return this.isLastSibling(parent);
  }

  isGrandparentLastSibling(node: any): boolean {
    const currentLevel = this.treeControl.getLevel(node);    
    if (currentLevel < 2) return false;
    const parent = this.getParentNode(node);
    if (!parent) return false;
    const grandparent = this.getParentNode(parent);
    if (!grandparent) return false;
    return this.isLastSibling(grandparent);
  }

  isGreatGrandparentLastSibling(node: any): boolean {
    if (this.treeControl.getLevel(node) < 3) return false;
    const parent = this.getParentNode(node);
    if (!parent) return false;
    const grandparent = this.getParentNode(parent);
    if (!grandparent) return false;
    const greatGrandparent = this.getParentNode(grandparent);
    if (!greatGrandparent) return false;
    return this.isLastSibling(greatGrandparent);
  }

  public isFirstSiblingOnLvl0(node: any): boolean {
    const all = this.treeControl.dataNodes;
    const roots = all.filter(n => this.treeControl.getLevel(n) === 0);
    if (!roots.length) {
      return false;
    }
    return roots[0] === node;
  }

  getRoleIndex(id: number): number {
    return this.userSubprojectsRoles.findIndex(sp => sp.id === id);
  }

  onClickDropdown(){
    this.inputElement.nativeElement.focus();
    this.showDropdown = true;
  }

  trackByProjectId(index: number, projectId: number): number {
      return projectId;
  }
  onFocusInput() {
    this.showDropdown = true;
    setTimeout(() => {
      if(this.uniqueId=="document-detail-subprojects"){
        this.onInputFocused.emit();
        return;
      }
      this.adjustDropdownPosition();
    }, 150);
  }

  adjustDropdownPosition() {
    setTimeout(() => {
      if (!this.dropdownRef?.nativeElement || this.uniqueId !== 'snackbar-subprojects') return;
      const dropdown = this.dropdownRef.nativeElement as HTMLElement;
      const height = dropdown.offsetHeight;
      let topPx: number;
      if (height >= 290) {
        topPx = -288;
      } else if (height >= 250) {
        topPx = -240;
      } else if (height >= 200) {
        topPx = -190;
      } else if (height >= 150) {
        topPx = -140;
      } else if (height >= 100) {
        topPx = -90;
      } else {
        topPx = -50;
      }
      dropdown.style.top = `${topPx}px`;
    });
  }
}
